import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { motion } from 'framer-motion';
import {
  MenuBookOutlined,
  DescriptionOutlined,
  LocalHospitalOutlined,
  PhoneAndroidOutlined,
  BarChartOutlined,
  PsychologyOutlined,
  SchoolOutlined,
  BadgeOutlined
} from '@mui/icons-material';
import './Services.css';

const Services = () => {
  const services = [
    {
      title: 'E-Logbook',
      description: 'NMC PGMER 2023-compliant digital logbook for PG medical students, built exclusively for your college: your domain, your app, your data.',
      icon: MenuBookOutlined,
      color: '#51A8B1',
      link: '/e-logbook'
    },
    {
      title: 'Electronic Health Records (EHR/EMR)',
      description: 'Comprehensive digital patient record systems designed for seamless clinical workflows and enhanced care coordination.',
      icon: DescriptionOutlined,
      color: '#A8CF45'
    },
    {
      title: 'Hospital Management Systems',
      description: 'End-to-end hospital operations management covering appointments, billing, inventory, and staff coordination.',
      icon: LocalHospitalOutlined,
      color: '#51A8B1'
    },
    {
      title: 'Mobile Health Applications',
      description: 'Patient-centric mobile solutions enabling remote monitoring, telemedicine, and healthcare access on-the-go.',
      icon: PhoneAndroidOutlined,
      color: '#A8CF45'
    },
    {
      title: 'Reporting & Compliance Tools',
      description: 'Automated regulatory compliance and advanced analytics for data-driven healthcare decision making.',
      icon: BarChartOutlined,
      color: '#51A8B1'
    },
    {
      title: 'AI-Driven Patient Care',
      description: 'Intelligent predictive analytics and machine learning for personalized patient care and clinical decision support.',
      icon: PsychologyOutlined,
      color: '#A8CF45'
    },
    {
      title: 'Learning Management Systems',
      description: 'Advanced educational platforms for medical colleges with interactive content, assessments, and progress tracking.',
      icon: SchoolOutlined,
      color: '#51A8B1'
    },
    {
      title: 'Hospital HR System',
      description: 'Staff records, shift scheduling, payroll and credentialing in one place, built for the pace of a hospital, not a generic office.',
      icon: BadgeOutlined,
      color: '#A8CF45'
    }
  ];

  return (
    <Box 
      id="services-section" 
      className="services-section" 
      sx={{ 
        py: 10, 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9f5f7 100%)', 
        position: 'relative', 
        overflow: 'hidden',
        scrollMarginTop: '20px'
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontFamily: 'Quicksand, sans-serif',
              fontWeight: 700,
              color: '#51A8B1',
              mb: 2,
            }}
          >
            Our Solutions
          </Typography>
          <Typography
            variant="h5"
            align="center"
            sx={{
              fontFamily: 'Quicksand, sans-serif',
              color: '#666',
              mb: 8,
              maxWidth: '800px',
              margin: '0 auto 64px auto',
            }}
          >
            Cutting-edge healthcare technology solutions built on the foundation of innovation, 
            reliability, and patient-centered care.
          </Typography>
        </motion.div>

        {/* DNA Helix Container */}
        <div className="dna-helix-container">
          {/* DNA Backbone Lines */}
          <svg className="dna-backbone" viewBox="0 0 100 1200" preserveAspectRatio="none">
            <path
              className="dna-strand strand-1"
              d="M 20 0 Q 35 100 20 200 Q 5 300 20 400 Q 35 500 20 600 Q 5 700 20 800 Q 35 900 20 1000 Q 5 1100 20 1200"
              fill="none"
              stroke="#51A8B1"
              strokeWidth="2"
              opacity="0.3"
            />
            <path
              className="dna-strand strand-2"
              d="M 80 0 Q 65 100 80 200 Q 95 300 80 400 Q 65 500 80 600 Q 95 700 80 800 Q 65 900 80 1000 Q 95 1100 80 1200"
              fill="none"
              stroke="#A8CF45"
              strokeWidth="2"
              opacity="0.3"
            />
          </svg>

          {/* Service Cards along DNA */}
          <div className="services-dna-grid">
            {services.map((service, index) => {
              const CardTag = service.link ? motion.a : motion.div;
              return (
                <CardTag
                  key={index}
                  className={`service-card ${index % 2 === 0 ? 'card-left' : 'card-right'}`}
                  whileHover={{ scale: 1.05, y: -5 }}
                  {...(service.link
                    ? { href: service.link, style: { textDecoration: 'none', color: 'inherit', cursor: 'pointer' } }
                    : {})}
                >
                  <div className="service-connector" style={{ borderColor: service.color }}></div>
                  <div className="service-base-pair" style={{ background: service.color }}></div>

                  <div className="service-content">
                    <div className="service-icon" style={{ background: `${service.color}15` }}>
                      <service.icon style={{ fontSize: '3rem', color: service.color }} />
                    </div>
                    <h3 className="service-title" style={{ color: service.color }}>
                      {service.title}
                    </h3>
                    <p className="service-description">{service.description}</p>
                    {service.link && (
                      <span className="service-link-cta" style={{ color: service.color }}>
                        Explore E-Logbook →
                      </span>
                    )}
                  </div>
                </CardTag>
              );
            })}
          </div>
        </div>

        {/* Decorative DNA Elements */}
        <div className="dna-decoration dna-decoration-1"></div>
        <div className="dna-decoration dna-decoration-2"></div>
      </Container>
    </Box>
  );
};

export default Services;